import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";

import { readCookie } from "@/utils/cookies";
import { getUrlParameter } from "@/utils/general";

/**
 * Registered users: all GraphQL requests are authorized via the gatsbytoken cookie
 * (sent as Bearer token). Guest checkout uses the masked_id cookie as the cart identifier
 * and does not use gatsbytoken for cart/checkout requests.
 */
const httpLink = new HttpLink({
  uri: process.env.NEXT_PUBLIC_MAGENTO_URL + "graphql",
});
const searchLink = new HttpLink({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_SEARCH,
});

const authMiddleware = new ApolloLink((operation, forward) => {
  const customHeaders = operation.getContext()?.headers ? operation.getContext().headers : {};

  const gatsbytoken =
    getUrlParameter("token") !== "" ? getUrlParameter("token") : readCookie("gatsbytoken");

  /* add the bearer token; preserve custom authorization if explicitly set in context */
  operation.setContext({
    headers: {
      authorization: `Bearer ${gatsbytoken}`,
      ...customHeaders,
    },
  });
  return forward(operation);
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.split(
    (operation) => operation.getContext()?.clientName === "searchLink",
    authMiddleware.concat(searchLink),
    authMiddleware.concat(httpLink)
  ),
});

export default client;

/**
 * Guest checkout client: never attaches a gatsbytoken bearer, even if a stale
 * one is sitting in the browser (leftover login on a shared device, expired
 * server-side session). A stale customer bearer sent alongside a guest cart_id
 * makes Magento throw "The current user cannot perform operations on cart",
 * which fails guestCartQuery and bounces the guest back to the cart page.
 */
export const guestApolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.split(
    (operation) => operation.getContext()?.clientName === "searchLink",
    searchLink,
    httpLink
  ),
});
